import { useEffect, useState } from "react";
import { api, withDlToken, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, Download, Loader2, ReceiptText, RotateCcw, FileEdit, Wallet, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = [
  { v: "starter", label: "Essentiel (49 €)" },
  { v: "pro", label: "Pro (99 €)" },
  { v: "business", label: "Premium (149 €)" },
];
const COUNTRIES = ["FR", "BE", "IT", "ES", "NL", "DE", "LU", "CA", "CH", "MA", "AE", "US", "GB"];
const TYPE_LABEL = { invoice: "Facture", credit_note: "Avoir", corrective: "Rectificative" };
const STATUS_LABEL = {
  paid: { t: "Payée", c: "bg-emerald-100 text-emerald-700" },
  refunded: { t: "Remboursée", c: "bg-red-100 text-red-700" },
  partially_refunded: { t: "Rembours. partiel", c: "bg-amber-100 text-amber-700" },
  cancelled: { t: "Annulée", c: "bg-slate-200 text-slate-600" },
};
const eur = (v) => `${Number(v).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return "—"; } };

export default function AdminInvoices({ clients = [] }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [simOpen, setSimOpen] = useState(false);
  const [sim, setSim] = useState({ client_id: "", plan: "pro", customer_type: "business", country: "FR", vat_number: "", billing_name: "", billing_address: "" });
  const [action, setAction] = useState(null); // {kind:'credit'|'refund'|'rectify', inv}
  const [form, setForm] = useState({ amount_ttc: "", reason: "", plan: "" });

  const load = () => api.get("/admin/invoices").then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pdfUrl = (inv) => withDlToken(`${API}/invoices/${inv.id}/pdf`);
  const csvUrl = withDlToken(`${API}/admin/invoices/export.csv`);

  const doSimulate = async () => {
    if (!sim.client_id) { toast.error("Choisissez un client."); return; }
    setBusy("sim");
    try {
      const r = await api.post("/admin/invoices/simulate-payment", sim);
      toast.success(`Facture ${r.data.invoice.number} générée${r.data.email_sent ? " et envoyée par e-mail" : ""}.`);
      setSimOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const openAction = (kind, inv) => { setAction({ kind, inv }); setForm({ amount_ttc: "", reason: "", plan: inv.plan_id }); };
  const submitAction = async () => {
    const { kind, inv } = action;
    setBusy("action");
    try {
      if (kind === "credit") {
        const body = { reason: form.reason }; if (form.amount_ttc) body.amount_ttc = parseFloat(form.amount_ttc);
        await api.post(`/admin/invoices/${inv.id}/credit-note`, body);
        toast.success("Avoir émis.");
      } else if (kind === "refund") {
        const body = { reason: form.reason }; if (form.amount_ttc) body.amount_ttc = parseFloat(form.amount_ttc);
        await api.post(`/admin/invoices/${inv.id}/refund`, body);
        toast.success("Remboursement enregistré (avoir émis).");
      } else if (kind === "rectify") {
        const body = { reason: form.reason || "Facture rectificative" };
        if (form.plan) body.plan = form.plan;
        if (form.amount_ttc) body.amount_ttc = parseFloat(form.amount_ttc);
        await api.post(`/admin/invoices/${inv.id}/rectify`, body);
        toast.success("Facture rectificative émise (avoir + nouvelle facture).");
      }
      setAction(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-invoices-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl text-[#0F172A]"><ReceiptText className="h-5 w-5 text-[#C6A87C]" /> Factures & comptabilité</h2>
          <p className="mt-1 text-sm text-slate-500">Factures officielles uniques, avoirs, remboursements et rectificatives. Exportables pour la comptabilité.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/parametres-facturation" data-testid="admin-billing-settings-link" className="inline-flex h-10 items-center gap-1.5 rounded-full border border-slate-300 px-4 text-sm font-medium text-[#0F172A] hover:bg-slate-50">
            <Settings2 className="h-4 w-4" /> Paramètres de facturation
          </Link>
          <a href={csvUrl} data-testid="admin-invoices-csv" className="inline-flex h-10 items-center gap-1.5 rounded-full border border-slate-300 px-4 text-sm font-medium text-[#0F172A] hover:bg-slate-50">
            <Download className="h-4 w-4" /> Export CSV
          </a>
          <Button onClick={() => setSimOpen(true)} data-testid="admin-simulate-payment-btn" className="h-10 rounded-full bg-[#C6A87C] px-5 text-[#0F172A] hover:bg-[#b8986a]">
            <Wallet className="mr-1.5 h-4 w-4" /> Simuler un paiement réussi
          </Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><p className="text-xs text-slate-400">Documents</p><p className="mt-1 font-display text-2xl text-[#0F172A]" data-testid="inv-count">{data.count}</p></div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><p className="text-xs text-slate-400">CA TTC (net avoirs)</p><p className="mt-1 font-display text-2xl text-emerald-600" data-testid="inv-revenue">{eur(data.revenue_ttc)}</p></div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><p className="text-xs text-slate-400">TVA collectée</p><p className="mt-1 font-display text-2xl text-[#0F172A]" data-testid="inv-vat">{eur(data.vat_collected)}</p></div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2.5 font-medium">N°</th>
              <th className="px-3 py-2.5 font-medium">Type</th>
              <th className="px-3 py-2.5 font-medium">Date</th>
              <th className="px-3 py-2.5 font-medium">Client</th>
              <th className="px-3 py-2.5 font-medium">Formule</th>
              <th className="px-3 py-2.5 font-medium text-right">TTC</th>
              <th className="px-3 py-2.5 font-medium">Statut</th>
              <th className="px-3 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.invoices.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Aucune facture. Cliquez « Simuler un paiement réussi » pour tester.</td></tr>}
            {data.invoices.map((inv) => {
              const st = STATUS_LABEL[inv.status] || STATUS_LABEL.paid;
              return (
                <tr key={inv.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-invoice-${inv.number}`}>
                  <td className="px-3 py-2.5 font-medium text-[#0F172A]">{inv.number}</td>
                  <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs ${inv.type === "invoice" ? "bg-slate-100 text-slate-600" : inv.type === "credit_note" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{TYPE_LABEL[inv.type]}</span></td>
                  <td className="px-3 py-2.5 text-slate-500">{fmtDate(inv.issue_date)}</td>
                  <td className="px-3 py-2.5"><p className="text-[#0F172A]">{inv.client?.name}</p><p className="text-xs text-slate-400">{inv.client?.country} · {inv.client?.customer_type === "business" ? "Pro" : "Particulier"}</p></td>
                  <td className="px-3 py-2.5 text-slate-500">{inv.plan_name}</td>
                  <td className="px-3 py-2.5 text-right font-medium text-[#0F172A]">{inv.type === "credit_note" ? "-" : ""}{eur(inv.amount_ttc)}</td>
                  <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${st.c}`}>{st.t}</span></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <a href={pdfUrl(inv)} target="_blank" rel="noreferrer" title="Télécharger le PDF" data-testid={`invoice-pdf-${inv.number}`} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]"><FileText className="h-4 w-4" /></a>
                      {inv.type === "invoice" && inv.status !== "cancelled" && (
                        <>
                          <button title="Émettre un avoir" data-testid={`invoice-credit-${inv.number}`} onClick={() => openAction("credit", inv)} className="rounded-md p-1.5 text-slate-500 hover:bg-amber-50 hover:text-amber-700"><ReceiptText className="h-4 w-4" /></button>
                          <button title="Rembourser" data-testid={`invoice-refund-${inv.number}`} onClick={() => openAction("refund", inv)} className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700"><RotateCcw className="h-4 w-4" /></button>
                          <button title="Facture rectificative" data-testid={`invoice-rectify-${inv.number}`} onClick={() => openAction("rectify", inv)} className="rounded-md p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700"><FileEdit className="h-4 w-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Simuler un paiement */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent data-testid="simulate-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Simuler un paiement réussi (test)</DialogTitle>
          <DialogDescription>Génère une facture officielle unique, sans paiement réel.</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500">Génère une facture officielle unique comme après un paiement Stripe réussi : PDF + e-mail + espace client + espace admin. Aucun paiement réel.</p>
          <div className="grid gap-3 py-2">
            <div><Label>Client</Label>
              <Select value={sim.client_id} onValueChange={(v) => setSim((s) => ({ ...s, client_id: v }))}>
                <SelectTrigger className="mt-1 h-10" data-testid="sim-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.email} — {c.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Formule</Label>
                <Select value={sim.plan} onValueChange={(v) => setSim((s) => ({ ...s, plan: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="sim-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLANS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Type de client</Label>
                <Select value={sim.customer_type} onValueChange={(v) => setSim((s) => ({ ...s, customer_type: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="sim-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="business">Professionnel</SelectItem><SelectItem value="individual">Particulier</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pays</Label>
                <Select value={sim.country} onValueChange={(v) => setSim((s) => ({ ...s, country: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="sim-country"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>N° TVA intracom. (pro UE)</Label><Input data-testid="sim-vat" value={sim.vat_number} onChange={(e) => setSim((s) => ({ ...s, vat_number: e.target.value }))} placeholder="BE0123456789" className="mt-1 h-10" /></div>
            </div>
            <div><Label>Raison sociale / nom facturé</Label><Input data-testid="sim-name" value={sim.billing_name} onChange={(e) => setSim((s) => ({ ...s, billing_name: e.target.value }))} placeholder="(défaut : nom du compte)" className="mt-1 h-10" /></div>
            <div><Label>Adresse de facturation</Label><Input data-testid="sim-address" value={sim.billing_address} onChange={(e) => setSim((s) => ({ ...s, billing_address: e.target.value }))} placeholder="10 rue…, 75001 Paris" className="mt-1 h-10" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSimOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={doSimulate} disabled={busy === "sim"} data-testid="sim-submit" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
              {busy === "sim" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Générer la facture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avoir / Remboursement / Rectificative */}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent data-testid="invoice-action-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">
            {action?.kind === "credit" ? "Émettre un avoir" : action?.kind === "refund" ? "Rembourser la facture" : "Facture rectificative"}
          </DialogTitle>
          <DialogDescription>Opération comptable liée à une facture existante.</DialogDescription></DialogHeader>
          {action && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-slate-500">Facture concernée : <b className="text-[#0F172A]">{action.inv.number}</b> — {eur(action.inv.amount_ttc)} TTC.</p>
              {action.kind === "rectify" && (
                <div><Label>Nouvelle formule (optionnel)</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
                    <SelectTrigger className="mt-1 h-10" data-testid="rectify-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLANS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {action.kind !== "rectify" && (
                <div><Label>Montant TTC (vide = total {eur(action.inv.amount_ttc)})</Label>
                  <Input data-testid="action-amount" type="number" step="0.01" value={form.amount_ttc} onChange={(e) => setForm((f) => ({ ...f, amount_ttc: e.target.value }))} placeholder={action.inv.amount_ttc} className="mt-1 h-10" />
                </div>
              )}
              <div><Label>Motif</Label><Textarea data-testid="action-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Motif de l'avoir / remboursement / rectification" className="mt-1" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="rounded-full">Annuler</Button>
            <Button onClick={submitAction} disabled={busy === "action"} data-testid="action-submit" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
              {busy === "action" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
